'use client';

import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { Check, Facebook, Instagram, Linkedin, Mail, Send } from 'lucide-react';
import { type CSSProperties, useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { toast } from 'react-toastify';

interface ArticleActionsProps {
    accent: string;
    socialLinks: {
        facebook?: string | null;
        twitter?: string | null;
        instagram?: string | null;
        whatsapp?: string | null;
        linkedin?: string | null;
    };
}

export const ArticleActions = ({ accent, socialLinks }: ArticleActionsProps) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const updateProgress = () => {
            const scrollable = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(scrollable > 0 ? Math.min(100, (window.scrollY / scrollable) * 100) : 0);
        };
        updateProgress();
        window.addEventListener('scroll', updateProgress, { passive: true });
        return () => window.removeEventListener('scroll', updateProgress);
    }, []);

    const shares = [
        {
            label: 'Visit us on Facebook',
            icon: Facebook,
            href: socialLinks.facebook,
            color: '#1877F2',
            softColor: 'rgba(24,119,242,.45)',
        },
        {
            label: 'Visit us on X',
            icon: FaXTwitter,
            href: socialLinks.twitter,
            color: '#000000',
            softColor: 'rgba(0,0,0,.3)',
        },
        {
            label: 'Visit us on Instagram',
            icon: Instagram,
            href: socialLinks.instagram,
            color: '#E4405F',
            softColor: 'rgba(228,64,95,.45)',
        },
        {
            label: 'Visit our WhatsApp channel',
            icon: FaWhatsapp,
            href: socialLinks.whatsapp,
            color: '#25D366',
            softColor: 'rgba(37,211,102,.45)',
        },
        {
            label: 'Visit us on LinkedIn',
            icon: Linkedin,
            href: socialLinks.linkedin,
            color: '#0A66C2',
            softColor: 'rgba(10,102,194,.45)',
        },
    ].filter((item): item is typeof item & { href: string } => Boolean(item.href));

    return (
        <>
            <div className="fixed inset-x-0 top-0 z-[60] h-1 bg-transparent" aria-hidden="true">
                <div
                    className="h-full transition-[width] duration-100"
                    style={{ width: `${progress}%`, backgroundColor: accent }}
                />
            </div>
            {shares.length > 0 && (
                <div className="fixed bottom-24 left-3 z-30 hidden flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg xl:flex">
                    {shares.map(({ label, icon: Icon, href, color, softColor }) => (
                        <a
                            key={label}
                            href={href}
                            target="_blank"
                            rel="nofollow sponsored noopener noreferrer"
                            aria-label={label}
                            className="blog-social-link flex h-9 w-9 items-center justify-center transition duration-200 hover:-translate-y-0.5"
                            style={{ '--social-brand': color, '--social-soft': softColor } as CSSProperties}
                        >
                            <Icon size={18} />
                        </a>
                    ))}
                </div>
            )}
            <style jsx>{`
                .blog-social-link {
                    color: var(--social-brand);
                }
                .blog-social-link:hover {
                    color: var(--social-brand);
                    filter: drop-shadow(0 0 5px var(--social-soft));
                    opacity: 0.82;
                }
            `}</style>
        </>
    );
};

export const NewsletterSignup = ({ companyId, accent }: { companyId: string; accent: string }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    const subscribe = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!email.trim()) return toast.error('Please enter your email address.');
        setLoading(true);
        try {
            const response = await apiSubscribeNewsletter(companyId, email.trim());
            if (response?.status === 200 || response?.message?.toLowerCase().includes('subscribed')) {
                setSubscribed(true);
                setEmail('');
            } else {
                toast.info(response?.message || 'You are already subscribed.');
            }
        } catch {
            toast.error('Could not subscribe. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="relative mt-14 overflow-hidden rounded-[2rem] border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/70 to-blue-50 px-6 py-8 text-slate-950 shadow-[0_18px_50px_-35px_rgba(79,70,229,0.45)] md:px-9 md:py-9">
            <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-200/45 blur-3xl" />
            <div className="absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-blue-100/60 blur-3xl" />
            <div className="relative grid items-center gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(380px,1.1fr)]">
                <div>
                    <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-200">
                            <Mail size={18} />
                        </span>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-600">
                            The Savings Edit
                        </p>
                    </div>
                    <h2 className="max-w-lg text-2xl font-black leading-tight tracking-tight md:text-3xl">
                        Better deals, delivered before you shop.
                    </h2>
                    <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
                        Fresh promo codes, practical saving tips, and the week&apos;s strongest offers in one
                        useful email.
                    </p>
                </div>
                <div>
                    {subscribed ? (
                        <div className="flex min-h-16 items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700">
                            <Check size={18} /> You&apos;re subscribed—watch your inbox.
                        </div>
                    ) : (
                        <form
                            onSubmit={subscribe}
                            className="flex w-full flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_12px_35px_-22px_rgba(15,23,42,0.35)] sm:flex-row"
                        >
                            <label className="sr-only" htmlFor="blog-newsletter-email">
                                Email address
                            </label>
                            <input
                                id="blog-newsletter-email"
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                required
                                maxLength={254}
                                placeholder="Your email address"
                                className="h-12 min-w-0 flex-1 rounded-xl bg-transparent px-4 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl px-6 text-sm font-black text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ backgroundColor: accent }}
                            >
                                {loading ? (
                                    'Joining...'
                                ) : (
                                    <>
                                        Join free <Send size={15} />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-medium text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-500" />
                            No spam
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Check size={12} className="text-emerald-500" />
                            Unsubscribe anytime
                        </span>
                        <span>Weekly, not daily</span>
                    </div>
                </div>
            </div>
        </section>
    );
};
